import { DefaultInfo } from "components/NothingRight";
import React, { FC, useEffect, useMemo, useState } from "react";
import { Carousel } from "react-responsive-carousel";
import _ from "lodash";
import { Loader } from "ui/Loader";
import { useLazyQuery } from "@apollo/client";
import { GET_ACTIVE_PROGRAM_CLIENT, GET_ADVANTAGE_PROGRAM_FOR_CLIENT } from "graphql/Fidelity/queries";
import { Advantage, Program } from "generated/graphql";
import { useCurrentClient } from "hooks";
import { isSuccess } from "utils/https";
import AdvantageItem from "../components/Advantage";
import Banner from "../components/Banner";
import AdvantageDetails from "../containers/AdvantageDetails";
import { FidelityLayout, useFidelityStyles } from "../style";
import "react-responsive-carousel/lib/styles/carousel.min.css";
import DotIndicator from "../components/carousel/DotIndicator";
import PrevButton from "../components/carousel/PrevButton";
import NextButton from "../components/carousel/NextButton";
import Header from "../components/Header";
// eslint-disable-next-line import/order
import useText from "constants/text";
import UseProgramDialog from "../components/modals/UseProgramDialog";
import NoProgram from "../components/NoProgram";

const Fidelity: FC = (props) => {
  const styles = useFidelityStyles();
  const [currentAdvantage, setCurrentAdvantage] = useState<Advantage>(null);
  const [advantages, setAdvantages] = useState<Advantage[][]>([]);
  const [program, setProgram] = useState<Program>(null);
  const { client } = useCurrentClient();
  const [isDetailPage, setIsDetailPage] = useState<boolean>(false);

  const { text } = useText();

  const [getAdvantagesProgram, getAdvantagesProgramData] = useLazyQuery(GET_ADVANTAGE_PROGRAM_FOR_CLIENT, {
    fetchPolicy: "network-only",
  });
  const [getActiveProgram, getActiveProgramData] = useLazyQuery(GET_ACTIVE_PROGRAM_CLIENT);
  const [open, setOpen] = useState<boolean>(false);

  const onClose = () => {
    setCurrentAdvantage(null);
    setOpen(false);
  };

  const renderIndicator = (_: (e: React.MouseEvent | React.KeyboardEvent) => void, isSelected: boolean) => {
    return <DotIndicator isSelected={isSelected} />;
  };

  const renderArrowPrev = (handlePrev: () => void, hasPrev: boolean) => {
    return hasPrev && <PrevButton onClick={handlePrev} />;
  };
  const renderArrowNext = (handleNext: () => void, hasNext: boolean) => {
    return hasNext && <NextButton onClick={handleNext} />;
  };

  const onCardClick = (advantage: Advantage) => {
    setIsDetailPage(true);
    setCurrentAdvantage(advantage);
  };

  const handleUseAdvantage = (advantage: Advantage) => {
    setCurrentAdvantage(advantage);
  };
  const handleCancelAdvantage = () => {
    setIsDetailPage(false);
    setCurrentAdvantage(null);
  };
  useEffect(() => {
    client &&
      getActiveProgram({
        variables: {
          id: client?._id,
        },
      });
  }, [client]);
  useEffect(() => {
    if (isSuccess(getAdvantagesProgramData.data)) {
      setAdvantages(_.chunk(getAdvantagesProgramData.data?.results?.data, 3));
    }
  }, [getAdvantagesProgramData.data, open]);
  useEffect(() => {
    if (isSuccess(getActiveProgramData.data)) {
      setProgram(getActiveProgramData?.data?.results?.data);
    }
  }, [getActiveProgramData.data]);

  useEffect(() => {
    program &&
      getAdvantagesProgram({
        variables: {
          params: { clientId: client?._id, programId: program?._id },
        },
      });
  }, [program]);
  const bannerTitle = useMemo(() => {
    const lang = localStorage.getItem("i18nextLng");
    const programTitle = program?.title || "N/A";
    if (lang === "fr") {
      return `${text.fidelity_program.premium_advantage} ${programTitle}`;
    }
    return `${programTitle} ${text.fidelity_program.premium_advantage}`;
  }, [text, program]);
  return (
    <FidelityLayout>
      <div className="container">
        {!client?.ranking ? (
          <NoProgram />
        ) : (
          <>
            <div className="left">
              <Header />
              <div className="card-container">
                <Banner title={bannerTitle} imageUrl={program?.iconUrl} />
                <div>
                  {getAdvantagesProgramData.loading && <Loader size={40} />}
                  <Carousel
                    className={styles.carousel}
                    showArrows
                    showStatus={false}
                    renderArrowPrev={renderArrowPrev}
                    renderArrowNext={renderArrowNext}
                    renderIndicator={renderIndicator}
                  >
                    {advantages?.map((items: Advantage[], index: number) => (
                      <div className={styles.carouselContent} key={index}>
                        {items.map((advantage, indexItem: number) => (
                          <AdvantageItem
                            onClick={handleUseAdvantage}
                            setIsDetailPage={setIsDetailPage}
                            onCardClick={onCardClick}
                            setOpen={setOpen}
                            key={`advantage${advantage?._id}`}
                            advantage={advantage}
                          />
                        ))}
                      </div>
                    ))}
                  </Carousel>
                </div>
              </div>
            </div>
            <div className="right">
              {!isDetailPage ? (
                <DefaultInfo />
              ) : (
                <AdvantageDetails
                  advantage={currentAdvantage}
                  program={program}
                  onCancel={handleCancelAdvantage}
                  clientiId={client?._id}
                />
              )}
            </div>
          </>
        )}
      </div>
      <UseProgramDialog
        endDate={program?.endDate}
        clientId={client?._id}
        advantage={currentAdvantage}
        open={open}
        onClose={onClose}
      />
    </FidelityLayout>
  );
};
export default Fidelity;
